import { Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { newPropertyRow, type PropertyRow } from './line-properties'

interface LinePropertiesEditorProps {
  rows: PropertyRow[]
  onChange: (rows: PropertyRow[]) => void
}

/**
 * The free properties of a line, one row each (name, value, unit), in the order the detail under
 * the line prints them. A row with no name is free text.
 */
export function LinePropertiesEditor({ rows, onChange }: LinePropertiesEditorProps) {
  const update = (id: string, patch: Partial<PropertyRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)))

  return (
    <div className="space-y-2" data-testid="imported-line-properties">
      {rows.length > 0 && (
        <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_64px_32px] gap-2 text-xs text-muted-foreground sm:grid">
          <span>Nom</span>
          <span>Valeur</span>
          <span>Unité</span>
          <span />
        </div>
      )}
      {rows.map((row) => (
        <div
          key={row.id}
          className="grid grid-cols-[minmax(0,1fr)_56px_32px] items-center gap-2 border-b pb-2 last-of-type:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_64px_32px] sm:border-b-0 sm:pb-0"
          data-testid="imported-line-property-row"
          data-property-key={row.key}
        >
          <Input
            // Below sm the name and the delete button share the first line, the value and unit the second.
            className="order-1 col-span-2 sm:col-span-1"
            aria-label="Nom de la propriété"
            placeholder="Texte libre"
            value={row.key}
            maxLength={100}
            onChange={(e) => update(row.id, { key: e.target.value })}
            data-testid="imported-line-property-key-input"
          />
          <Input
            className="order-3 sm:order-2"
            aria-label={row.key ? `Valeur de ${row.key}` : 'Texte'}
            value={row.value}
            maxLength={2000}
            onChange={(e) => update(row.id, { value: e.target.value })}
            data-testid="imported-line-property-value-input"
          />
          <Input
            className="order-4 sm:order-3"
            aria-label={row.key ? `Unité de ${row.key}` : 'Unité'}
            placeholder="—"
            value={row.unit}
            maxLength={20}
            onChange={(e) => update(row.id, { unit: e.target.value })}
            data-testid="imported-line-property-unit-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="order-2 h-8 w-8 sm:order-4"
            aria-label={row.key ? `Supprimer ${row.key}` : 'Supprimer la propriété'}
            onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
            data-testid="imported-line-property-remove-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, newPropertyRow()])}
        data-testid="imported-line-property-add-btn"
      >
        <Plus className="mr-1 h-4 w-4" />
        Ajouter une propriété
      </Button>
    </div>
  )
}
